/**
 * Gate de assinatura — verifica o status do tenant e bloqueia se suspenso.
 *
 * Regras:
 *   - master sempre passa (mesmo impersonando suspended)
 *   - status 'suspended' ou 'canceled' → mostra tela bloqueada
 *   - outros status → libera (banner amarelo aparece se trial/past_due via AppLayout)
 */
import type { ReactNode } from 'react'
import { Pause, Phone, Mail, AlertCircle } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useTenant } from '@/hooks/useTenant'

export function SubscriptionGate({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth()
  const { tenant } = useTenant()

  // Master sempre passa (não bloqueia ninguém, nem si mesmo)
  if (user?.role === 'master') return <>{children}</>

  const status = tenant?.subscription?.status
  if (status !== 'suspended' && status !== 'canceled') return <>{children}</>

  const motivo = status === 'suspended'
    ? 'Sua conta está temporariamente suspensa por falta de pagamento.'
    : 'Sua conta foi cancelada.'

  return (
    <div className="min-h-screen bg-bg text-text-primary flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg bg-card border border-danger/40 rounded-3xl p-8 shadow-2xl text-center">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-danger/15 text-danger flex items-center justify-center mb-4">
          <Pause size={28} />
        </div>
        <h1 className="text-2xl font-extrabold mb-2">Acesso bloqueado</h1>
        <p className="text-sm text-text-secondary mb-1">{motivo}</p>
        <p className="text-sm text-text-secondary mb-6">
          Pra reativar, entra em contato:
        </p>

        <div className="bg-bg border border-border rounded-2xl p-4 space-y-2 text-left mb-6">
          <a
            href="https://wa.me/5514997867932"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 text-sm hover:text-accent"
          >
            <Phone size={16} className="text-accent" />
            <span><strong>WhatsApp:</strong> (14) 99786-7932</span>
          </a>
          <a
            href="mailto:suporte@onixeventos.com.br"
            className="flex items-center gap-3 text-sm hover:text-accent"
          >
            <Mail size={16} className="text-accent" />
            <span><strong>E-mail:</strong> suporte@onixeventos.com.br</span>
          </a>
        </div>

        <div className="bg-warning/10 border border-warning/40 rounded-xl px-3 py-2 text-xs text-warning flex items-start gap-2 mb-6 text-left">
          <AlertCircle size={14} className="shrink-0 mt-0.5" />
          <span>
            Seus dados continuam salvos. Quando a conta for reativada,
            tudo volta exatamente como estava — clientes, eventos, financeiro etc.
          </span>
        </div>

        <button
          onClick={() => signOut()}
          className="text-xs font-bold px-4 py-2 rounded-lg border border-border hover:bg-card-hover"
        >
          Sair da conta
        </button>
      </div>
    </div>
  )
}
